import { useState, useEffect, useCallback, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { EntityFormDialog } from '@/shared/components';
import { Separator } from '@/components/ui/separator';
import { ModalErrorBoundary } from '@/components/ErrorBoundary';
import { useHasRole } from '@/hooks/useSession';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useSensorCreateOptions, useCreateSensor } from '../hooks/useSensorEdit';
import { SensorIdentityFields } from './SensorIdentityFields';
import { SensorAssignmentFields } from './SensorAssignmentFields';
import { SensorStatusFlags } from './SensorStatusFlags';
import { SensorEquipmentFields } from './SensorEquipmentFields';
import { SensorTagsDescription } from './SensorTagsDescription';
import { SensorInvoicesSection } from './SensorInvoicesSection';
import { SensorGroupsField } from './SensorGroupsField';
import { SensorDailyLogFields } from './SensorDailyLogFields';
import type { SensorCreateForm, DailyLogForm } from '../types';

// =============================================================================
// Form Helpers
// =============================================================================

const EMPTY_FORM: SensorCreateForm = {
  name: '',
  customid: '',
  input_number: '',
  sensortype: '',
  usergroup: '',
  site: '',
  location: '',
  billable: false,
  maintenance: false,
  manufacturer: '',
  model: '',
  serialnumber: '',
  tags: [],
  description: '',
  invoices: [],
  sensorgroups: [],
  dailylog: { am: '', pm: '', threshold_min: '', threshold_max: '' },
};

// =============================================================================
// Component
// =============================================================================

interface CreateSensorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId?: string | null;
  requireOrganisation?: boolean;
}

export function CreateSensorDialog({
  open,
  onOpenChange,
  organisationId,
  requireOrganisation = false,
}: CreateSensorDialogProps) {
  const isAdmin = useHasRole('full_admin');
  const isSuperAdmin = useIsSuperAdmin();
  const resolvedOrganisationId = organisationId || undefined;
  const isMissingRequiredOrganisation = requireOrganisation && !resolvedOrganisationId;

  // Data
  const optionsQuery = useSensorCreateOptions(
    open && !isMissingRequiredOrganisation,
    resolvedOrganisationId,
  );
  const createMutation = useCreateSensor();

  // Form state
  const [form, setForm] = useState<SensorCreateForm>(EMPTY_FORM);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  const isValid = useMemo(() => {
    if (!form.name.trim()) return false;
    if (isMissingRequiredOrganisation) return false;
    if (!isAdmin && !form.usergroup) return false;
    return true;
  }, [form, isAdmin, isMissingRequiredOrganisation]);

  const handleFieldChange = useCallback(
    (field: string, value: string | boolean | string[] | DailyLogForm) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleCreate = async () => {
    const payload: SensorCreateForm = resolvedOrganisationId
      ? { ...form, organisation: resolvedOrganisationId }
      : form;
    await createMutation.mutateAsync(payload);
    onOpenChange(false);
  };

  const options = optionsQuery.data;
  const isLoading = optionsQuery.isLoading;

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="3xl"
      title="New Sensor"
      onSubmit={handleCreate}
      isSubmitting={createMutation.isPending}
      saveDisabled={!isValid || isLoading}
      saveLabel={createMutation.isPending ? 'Creating...' : 'Create'}
    >
      <ModalErrorBoundary>
          {isMissingRequiredOrganisation ? (
            <div className="py-8 text-center">
              <p className="text-destructive">
                This account is missing a legacy organisation link, so sensors cannot be created here.
              </p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-1/2" />
            </div>
          ) : options ? (
            <div className="grid grid-cols-2 gap-6 py-2">
              {/* Left Column: Identity + Assignment + Groups */}
              <div className="space-y-6">
                <SensorIdentityFields
                  name={form.name}
                  customid={form.customid}
                  input_number={form.input_number}
                  onChange={handleFieldChange}
                />
                <SensorAssignmentFields
                  sensortype={form.sensortype}
                  usergroup={form.usergroup}
                  site={form.site}
                  location={form.location}
                  options={options}
                  isAdmin={isAdmin}
                  onChange={handleFieldChange}
                />
                {options.sensorgroups && options.sensorgroups.length > 0 && (
                  <SensorGroupsField
                    selected={form.sensorgroups}
                    options={options.sensorgroups}
                    onChange={handleFieldChange}
                  />
                )}
              </div>

              {/* Right Column: Flags + Collapsible sections */}
              <div className="space-y-4">
                <SensorStatusFlags
                  billable={form.billable}
                  maintenance={form.maintenance}
                  archived={false}
                  hideCalibration
                  isSuperAdmin={isSuperAdmin}
                  onChange={handleFieldChange}
                />

                <Separator />

                <SensorDailyLogFields
                  dailylog={form.dailylog}
                  onChange={handleFieldChange}
                />

                <SensorEquipmentFields
                  manufacturer={form.manufacturer}
                  model={form.model}
                  serialnumber={form.serialnumber}
                  onChange={handleFieldChange}
                />

                <SensorTagsDescription
                  tags={form.tags}
                  description={form.description}
                  onChange={handleFieldChange}
                />

                {isSuperAdmin && (
                  <SensorInvoicesSection
                    invoices={form.invoices}
                    onChange={(invoices) => handleFieldChange('invoices', invoices)}
                  />
                )}
              </div>
            </div>
          ) : optionsQuery.error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">Failed to load form options.</p>
            </div>
          ) : null}
      </ModalErrorBoundary>
    </EntityFormDialog>
  );
}
